// pages/SocialHub.jsx
// DWI SocialHub M1 — drop a photo + the words you want, get 3 on-brand designs
// and captions, then post (or schedule) to connected Facebook Pages and
// Instagram accounts. While publishing is switched off, posts run as a dry run
// and nothing reaches Facebook or Instagram.
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { socialHubAPI, uploadToSignedUrl } from '../services/api';
import { Button, Card, Input, Select, Badge, Empty, Spinner } from '../components/ui';

const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const newRequestId = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `r${Date.now()}${Math.random().toString(36).slice(2, 10)}`);
const TEXTAREA = 'mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan';

const ITEM_STATUS = {
  draft:      ['Waiting for photo', 'gray'],
  generating: ['Designing…', 'amber'],
  ready:      ['Ready', 'green'],
  failed:     ['Failed', 'red'],
  archived:   ['Archived', 'gray'],
};
const POST_STATUS = {
  queued:      ['Queued', 'amber'],
  scheduled:   ['Scheduled', 'cyan'],
  publishing:  ['Sending…', 'amber'],
  published:   ['Published', 'green'],
  dry_run:     ['Dry run — not sent', 'purple'],
  failed:      ['Failed', 'red'],
  needs_check: ['Check the page', 'rose'],
  canceled:    ['Canceled', 'gray'],
};
const PLATFORM = { facebook: 'Facebook', instagram: 'Instagram' };

const TABS = [['create', 'Create'], ['posts', 'Posts'], ['accounts', 'Accounts'], ['brand', 'Brand kit']];

export default function SocialHub() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'create';
  const [status, setStatus] = useState(null);

  useEffect(() => { socialHubAPI.getStatus().then(setStatus).catch(err => toast.error(err.message)); }, []);

  if (!status) return <div className="py-16 flex justify-center"><Spinner /></div>;
  if (!status.enabled) {
    return <Card><Empty icon="📣" title="SocialHub is not available yet" description="Automated social media posting is coming soon." /></Card>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-text-primary">SocialHub</h1>
        <p className="text-sm text-text-muted">Drop a photo and your words — get designed posts for Facebook and Instagram.</p>
      </div>
      <StatusBanners status={status} />
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setParams({ tab: k })}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === k ? 'border-cyan text-cyan' : 'border-transparent text-text-muted hover:text-text-primary'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'create' && <CreateTab status={status} />}
      {tab === 'posts' && <PostsTab />}
      {tab === 'accounts' && <AccountsTab status={status} />}
      {tab === 'brand' && <BrandTab />}
    </div>
  );
}

function StatusBanners({ status }) {
  const notes = [];
  if (!status.publishing) notes.push(['purple', 'Dry-run mode', 'Posting is switched off. Posts go through every check but nothing is sent to Facebook or Instagram.']);
  if (!status.ai) notes.push(['gray', 'AI captions off', 'Captions and headlines are made from your own words.']);
  if (status.storageReady === false) notes.push(['red', 'Storage not set up', 'Photo storage (R2) is not configured on the server yet.']);
  if (status.metaReady === false) notes.push(['red', 'Meta app not set up', 'Facebook/Instagram connections are not configured on the server yet.']);
  if (!notes.length) return null;
  return (
    <div className="space-y-2">
      {notes.map(([color, title, text]) => (
        <div key={title} className="flex items-start gap-2 bg-bg-surface border border-border rounded-xl px-3 py-2">
          <Badge label={title} color={color} />
          <span className="text-xs text-text-secondary">{text}</span>
        </div>
      ))}
    </div>
  );
}

// ── Create ─────────────────────────────────────────────────────────────────
function CreateTab({ status }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await socialHubAPI.listItems()).items); } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) return <ItemEditor id={openId} status={status} onClose={() => { setOpenId(null); load(); }} />;

  return (
    <div className="space-y-4">
      <DropForm onCreated={id => setOpenId(id)} />
      <Card>
        <h3 className="text-text-primary font-bold text-sm mb-3">Recent</h3>
        {!items ? <Spinner /> : items.length === 0
          ? <Empty icon="🖼️" title="Nothing yet" description="Your first post will show here." />
          : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {items.map(it => (
                <button key={it.id} onClick={() => setOpenId(it.id)} className="text-left bg-bg-elevated border border-border rounded-xl overflow-hidden hover:border-cyan">
                  {it.designs[0]?.url
                    ? <img src={it.designs[0].url} alt="" className="w-full aspect-[4/5] object-cover" />
                    : <div className="w-full aspect-[4/5] flex items-center justify-center text-text-muted text-xs">{ITEM_STATUS[it.status]?.[0]}</div>}
                  <div className="p-2 space-y-1">
                    <Badge label={ITEM_STATUS[it.status]?.[0] || it.status} color={ITEM_STATUS[it.status]?.[1]} />
                    <div className="text-xs text-text-secondary line-clamp-2">{it.headline || it.text}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}

function DropForm({ onCreated }) {
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [aspect, setAspect] = useState('4:5');
  const [busy, setBusy] = useState(false);
  const preview = file ? URL.createObjectURL(file) : null;
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Choose a photo first');
    if (!text.trim()) return toast.error('Write what the post should say');
    setBusy(true);
    try {
      const { item, uploadUrl } = await socialHubAPI.createItem({ text, aspect, mime: file.type, bytes: file.size });
      await uploadToSignedUrl(uploadUrl, file);
      await socialHubAPI.confirmUpload(item.id);
      toast.success('Designing your post…');
      setFile(null); setText('');
      onCreated(item.id);
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  return (
    <Card>
      <form onSubmit={submit} className="grid sm:grid-cols-[180px_1fr] gap-4">
        <label className="cursor-pointer bg-bg-elevated border border-dashed border-border rounded-xl flex items-center justify-center aspect-[4/5] overflow-hidden hover:border-cyan">
          {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-text-muted text-center px-3">Tap to add a photo<br />(JPEG, PNG or WebP)</span>}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => setFile(e.target.files[0] || null)} />
        </label>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-text-secondary">What should the post say?</label>
            <textarea rows={5} maxLength={2000} value={text} onChange={e => setText(e.target.value)} className={TEXTAREA}
              placeholder="e.g. Pay for internet in seconds with DWIPay Wallet. No queues, no cash. Top up with MTN or Airtel." />
          </div>
          <Select label="Shape" value={aspect} onChange={e => setAspect(e.target.value)}>
            <option value="4:5">Portrait 4:5 (best for Instagram + Facebook)</option>
            <option value="1:1">Square 1:1</option>
            <option value="9:16">Tall 9:16 (stories)</option>
          </Select>
          <Button type="submit" loading={busy}>Create designs</Button>
        </div>
      </form>
    </Card>
  );
}

function ItemEditor({ id, status, onClose }) {
  const [item, setItem] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [design, setDesign] = useState(null);
  const [picked, setPicked] = useState([]);
  const [caps, setCaps] = useState({ facebook: '', instagram: '' });
  const [head, setHead] = useState({ headline: '', subtext: '' });
  const [whenAt, setWhenAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ item: it }, { posts: ps }] = await Promise.all([socialHubAPI.getItem(id), socialHubAPI.listPosts({ contentItem: id })]);
      setItem(it); setPosts(ps);
      return it;
    } catch (err) { toast.error(err.message); return null; }
  }, [id]);

  useEffect(() => {
    load().then(it => {
      if (!it) return;
      setCaps({ facebook: it.captions?.facebook || '', instagram: it.captions?.instagram || '' });
      setHead({ headline: it.headline || '', subtext: it.subtext || '' });
      setDesign(it.designs[0]?.id || null);
    });
    socialHubAPI.listAccounts().then(r => setAccounts(r.accounts.filter(a => a.status === 'active'))).catch(() => {});
  }, [load]);

  // While designs are being made, check every 3 s.
  useEffect(() => {
    if (!item || item.status !== 'generating') return undefined;
    const t = setInterval(async () => {
      const it = await load();
      if (it && it.status !== 'generating') {
        setCaps({ facebook: it.captions?.facebook || '', instagram: it.captions?.instagram || '' });
        setHead({ headline: it.headline || '', subtext: it.subtext || '' });
        setDesign(it.designs[0]?.id || null);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [item, load]);

  if (!item) return <div className="py-16 flex justify-center"><Spinner /></div>;

  const redesign = async () => {
    setBusy(true);
    try { setItem((await socialHubAPI.updateItem(id, head)).item); } catch (err) { toast.error(err.message); }
    setBusy(false);
  };
  const redoCaptions = async () => {
    setBusy(true);
    try { setItem((await socialHubAPI.redoCaptions(id)).item); } catch (err) { toast.error(err.message); }
    setBusy(false);
  };
  const send = async () => {
    if (!design) return toast.error('Pick a design');
    if (!picked.length) return toast.error('Pick at least one account');
    setBusy(true);
    try {
      await socialHubAPI.updateItem(id, { captions: caps });
      const { posts: created } = await socialHubAPI.schedule(id, {
        designId: design, accountIds: picked, requestId: newRequestId(),
        when: whenAt ? new Date(whenAt).toISOString() : undefined,
      });
      toast.success(whenAt ? `Scheduled for ${created.length} account(s)` : (status.publishing ? 'Sending now…' : 'Queued as a dry run'));
      setPicked([]);
      load();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };
  const archive = async () => {
    try { await socialHubAPI.archiveItem(id); onClose(); } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>← Back</Button>
        <Badge label={ITEM_STATUS[item.status]?.[0] || item.status} color={ITEM_STATUS[item.status]?.[1]} />
      </div>

      {item.status === 'generating' && <Card><div className="flex items-center gap-3"><Spinner /> <span className="text-sm text-text-secondary">Making your designs and captions…</span></div></Card>}
      {item.error && <div className="text-xs text-amber bg-bg-surface border border-border rounded-xl px-3 py-2">{item.error}</div>}

      {item.designs.length > 0 && (
        <Card>
          <h3 className="text-text-primary font-bold text-sm mb-3">1. Pick a design</h3>
          <div className="grid grid-cols-3 gap-3">
            {item.designs.map(d => (
              <button key={d.id} onClick={() => setDesign(d.id)}
                className={`rounded-xl overflow-hidden border-2 ${design === d.id ? 'border-cyan' : 'border-transparent'}`}>
                <img src={d.url} alt={d.template} className="w-full" />
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <Input label="Headline on the image" value={head.headline} maxLength={120} onChange={e => setHead({ ...head, headline: e.target.value })} />
            <Input label="Second line" value={head.subtext} maxLength={120} onChange={e => setHead({ ...head, subtext: e.target.value })} />
          </div>
          <div className="mt-3"><Button size="sm" variant="secondary" loading={busy} onClick={redesign}>Redraw designs</Button></div>
        </Card>
      )}

      {item.status === 'ready' && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-text-primary font-bold text-sm">2. Captions</h3>
            <Button size="xs" variant="ghost" loading={busy} onClick={redoCaptions}>{status.ai ? '✨ Rewrite with AI' : 'Rebuild from my words'}</Button>
          </div>
          {['facebook', 'instagram'].map(p => (
            <div key={p} className="mb-3">
              <label className="text-xs font-semibold text-text-secondary">{PLATFORM[p]}</label>
              <textarea rows={4} maxLength={2200} value={caps[p]} onChange={e => setCaps({ ...caps, [p]: e.target.value })} className={TEXTAREA} />
            </div>
          ))}
          {item.hashtags?.length > 0 && <div className="text-xs text-cyan">{item.hashtags.map(t => `#${t}`).join(' ')}</div>}
        </Card>
      )}

      {item.status === 'ready' && (
        <Card>
          <h3 className="text-text-primary font-bold text-sm mb-3">3. Where and when</h3>
          {accounts.length === 0
            ? <p className="text-sm text-text-muted">No accounts connected yet — open the Accounts tab.</p>
            : (
              <div className="space-y-2 mb-3">
                {accounts.map(a => (
                  <label key={a.id} className="flex items-center gap-2 text-sm text-text-primary">
                    <input type="checkbox" checked={picked.includes(a.id)}
                      onChange={e => setPicked(e.target.checked ? [...picked, a.id] : picked.filter(x => x !== a.id))} />
                    {PLATFORM[a.platform]} · {a.displayName}{a.username ? ` (@${a.username})` : ''}
                  </label>
                ))}
              </div>
            )}
          <Input label="Schedule (leave empty to post now)" type="datetime-local" value={whenAt} onChange={e => setWhenAt(e.target.value)} />
          <div className="flex gap-2 mt-4">
            <Button loading={busy} onClick={send} disabled={!accounts.length}>{whenAt ? 'Schedule' : status.publishing ? 'Post now' : 'Post now (dry run)'}</Button>
            <Button variant="ghost" onClick={archive}>Archive</Button>
          </div>
        </Card>
      )}

      {posts.length > 0 && <Card><PostList posts={posts} onChange={load} /></Card>}
    </div>
  );
}

// ── Posts ──────────────────────────────────────────────────────────────────
function PostsTab() {
  const [posts, setPosts] = useState(null);
  const load = useCallback(async () => {
    try { setPosts((await socialHubAPI.listPosts()).posts); } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);
  if (!posts) return <div className="py-16 flex justify-center"><Spinner /></div>;
  if (!posts.length) return <Card><Empty icon="🗓️" title="No posts yet" description="Create a design and send it to an account." /></Card>;
  return <Card><PostList posts={posts} onChange={load} /></Card>;
}

function PostList({ posts, onChange }) {
  const act = async (fn) => { try { await fn(); onChange(); } catch (err) { toast.error(err.message); } };
  return (
    <div className="divide-y divide-border/40">
      {posts.map(p => (
        <div key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-text-primary font-semibold">{PLATFORM[p.platform]} · {p.accountName || 'removed account'}</div>
            <div className="text-xs text-text-muted">{p.status === 'scheduled' ? `For ${when(p.scheduledAt)}` : when(p.publishedAt || p.scheduledAt)}</div>
            {p.lastError?.message && <div className="text-xs text-red">{p.lastError.message}</div>}
            {p.permalink && <a href={p.permalink} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan">View post ↗</a>}
          </div>
          <div className="flex items-center gap-2">
            <Badge label={POST_STATUS[p.status]?.[0] || p.status} color={POST_STATUS[p.status]?.[1]} />
            {['queued', 'scheduled', 'failed'].includes(p.status) && <Button size="xs" variant="ghost" onClick={() => act(() => socialHubAPI.cancelPost(p.id))}>Cancel</Button>}
            {p.status === 'needs_check' && (
              <>
                <Button size="xs" variant="success" onClick={() => act(() => socialHubAPI.resolvePost(p.id, 'published'))}>It posted</Button>
                <Button size="xs" variant="danger" onClick={() => act(() => socialHubAPI.resolvePost(p.id, 'failed'))}>It didn't</Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Accounts ───────────────────────────────────────────────────────────────
function AccountsTab({ status }) {
  const [params] = useSearchParams();
  const [accounts, setAccounts] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setAccounts((await socialHubAPI.listAccounts()).accounts); } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get('error')) toast.error(params.get('error'));
    if (params.get('connected')) {
      const [fb, ig] = params.get('connected').split(',');
      toast.success(`Connected ${fb} Facebook Page(s) and ${ig} Instagram account(s)`);
    }
  }, [params]);

  const connect = async () => {
    setBusy(true);
    try { window.location.href = (await socialHubAPI.connectMeta()).url; } catch (err) { toast.error(err.message); setBusy(false); }
  };
  const act = async (fn) => { try { await fn(); load(); } catch (err) { toast.error(err.message); } };
  const canConnect = status.platforms.facebook || status.platforms.instagram;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-text-primary font-bold">Facebook Pages & Instagram</div>
            <div className="text-xs text-text-muted">You log in on Facebook itself — DWI never sees your password. Instagram must be a Business or Creator account linked to your Page.</div>
          </div>
          <Button size="sm" loading={busy} disabled={!canConnect} onClick={connect}>Connect with Facebook</Button>
        </div>
        {!canConnect && <p className="text-xs text-amber mt-2">Facebook and Instagram connections are switched off.</p>}
      </Card>
      <Card>
        {!accounts ? <Spinner /> : accounts.length === 0
          ? <Empty icon="🔗" title="No accounts connected" description="Connect your Facebook Page to post there and to its linked Instagram account." />
          : (
            <div className="divide-y divide-border/40">
              {accounts.map(a => (
                <div key={a.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {a.avatarUrl ? <img src={a.avatarUrl} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-bg-elevated" />}
                    <div>
                      <div className="text-sm text-text-primary font-semibold">{a.displayName}{a.username ? ` (@${a.username})` : ''}</div>
                      <div className="text-xs text-text-muted">{PLATFORM[a.platform]}{a.lastError ? ` · ${a.lastError}` : ''}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge label={a.status.replace('_', ' ')} color={a.status === 'active' ? 'green' : a.status === 'disabled' ? 'gray' : 'red'} />
                    {a.status === 'active' && <Button size="xs" variant="ghost" onClick={() => act(() => socialHubAPI.setAccount(a.id, false))}>Pause</Button>}
                    {a.status === 'disabled' && <Button size="xs" variant="ghost" onClick={() => act(() => socialHubAPI.setAccount(a.id, true))}>Resume</Button>}
                    <Button size="xs" variant="danger" onClick={() => { if (window.confirm(`Disconnect ${a.displayName}? Queued posts for it will be canceled.`)) act(() => socialHubAPI.disconnect(a.id)); }}>Disconnect</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}

// ── Brand kit ──────────────────────────────────────────────────────────────
function BrandTab() {
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    socialHubAPI.getBrand().then(r => setKit(r.kit || {
      name: 'My brand', handle: '', tone: 'Friendly, clear and confident.', ctaText: '', defaultHashtags: [],
      colors: { primary: '#0B3D91', accent: '#F5A623', text: '#FFFFFF' },
    })).catch(err => toast.error(err.message));
  }, []);
  if (!kit) return <div className="py-16 flex justify-center"><Spinner /></div>;

  const set = (k, v) => setKit({ ...kit, [k]: v });
  const setColor = (k, v) => setKit({ ...kit, colors: { ...kit.colors, [k]: v } });
  const save = async () => {
    setBusy(true);
    try {
      let saved = (await socialHubAPI.saveBrand(kit)).kit;
      if (logo) {
        const { key, uploadUrl } = await socialHubAPI.startLogo({ mime: logo.type, bytes: logo.size });
        await uploadToSignedUrl(uploadUrl, logo);
        saved = (await socialHubAPI.confirmLogo(key)).kit;
        setLogo(null);
      }
      setKit(saved);
      toast.success('Brand kit saved — new designs will use it');
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  return (
    <Card>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Brand name" value={kit.name} maxLength={80} onChange={e => set('name', e.target.value)} />
        <Input label="Handle on designs" value={kit.handle} maxLength={60} placeholder="@dwifintech" onChange={e => set('handle', e.target.value)} />
        <Input label="Call to action" value={kit.ctaText} maxLength={60} placeholder="Download the DWI app" onChange={e => set('ctaText', e.target.value)} />
        <Input label="Default hashtags" value={(kit.defaultHashtags || []).join(' ')} placeholder="DWIFintech Uganda"
          onChange={e => set('defaultHashtags', e.target.value.split(/[\s,]+/).filter(Boolean))} />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-text-secondary">Tone of voice</label>
        <textarea rows={2} maxLength={300} value={kit.tone} onChange={e => set('tone', e.target.value)} className={TEXTAREA} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        {[['primary', 'Main colour'], ['accent', 'Accent'], ['text', 'Text']].map(([k, label]) => (
          <label key={k} className="text-xs font-semibold text-text-secondary flex flex-col gap-1">
            {label}
            <input type="color" value={kit.colors[k]} onChange={e => setColor(k, e.target.value.toUpperCase())} className="w-full h-10 rounded-lg bg-transparent" />
          </label>
        ))}
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-text-secondary">Logo {kit.hasLogo && <Badge label="saved" color="green" />}</label>
        <input type="file" accept="image/png,image/jpeg,image/webp" className="block mt-1 text-sm text-text-secondary" onChange={e => setLogo(e.target.files[0] || null)} />
        <p className="text-xs text-text-muted mt-1">A PNG with a transparent background looks best.</p>
      </div>
      <Button className="mt-4" loading={busy} onClick={save}>Save brand kit</Button>
    </Card>
  );
}
